"use client"

import { useEffect, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import {
  AlertTriangle,
  ArchiveRestore,
  CheckCircle2,
  Loader2,
  Trash2,
  XCircle,
} from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { useAuth } from "@/hooks/use-auth"
import { currentTenantQueryPrefix } from "@/hooks/queries/current-tenant"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"


/**
 * Super-user-only lifecycle controls (archive / restore / purge) for any
 * tenant from the admin Tenant Management page. Each action posts to its
 * dedicated endpoint and surfaces the structured `cleanup_summary` to the
 * operator on success — restore in particular needs it because a cleaner
 * `ok: false` keeps the `tenant_subscriptions` row, which the super_user
 * must know about before assigning a fresh plan.
 *
 * Dialogs intentionally use `Dialog` (not `AlertDialog`) — `AlertDialogAction`
 * auto-closes synchronously and would dismiss the dialog mid-async, hiding
 * the cleanup_summary the operator is supposed to read.
 */

export interface LifecycleTenant {
  id: string
  name: string
  archived_at: string | null
  archive_scheduled_purge_at: string | null
}

type LifecycleAction = "archive" | "restore" | "purge" | null

interface CleanupStep {
  step: string
  ok: boolean
  error?: string
  details?: Record<string, unknown>
}

interface LifecycleActionsProps {
  tenant: LifecycleTenant
  /**
   * True when this row is the operator's own active tenant. The parent
   * derives it from `useAuth().user` (impersonated > tenant_id) so each
   * row stays render-cheap rather than subscribing to the tenant query per row.
   */
  isOwnTenant: boolean
  // Verb lets the parent keep the just-mutated row visible after refetch.
  onChanged: (completed?: Exclude<LifecycleAction, null>) => void
}

export function LifecycleActions({ tenant, isOwnTenant, onChanged }: LifecycleActionsProps) {
  const tUi = useTranslations("TenantManagement.TenantManagementLifecycleActions")
  const tCommonUi = useTranslations("Common")

  const { fetchWithAuth } = useAuth()
  const queryClient = useQueryClient()

  const [action, setAction] = useState<LifecycleAction>(null)
  const [confirmName, setConfirmName] = useState("")
  const [confirmPurge, setConfirmPurge] = useState("")
  const [confirmIrreversible, setConfirmIrreversible] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resultSummary, setResultSummary] = useState<CleanupStep[] | null>(null)
  const [completedAction, setCompletedAction] = useState<Exclude<LifecycleAction, null> | null>(null)

  const isArchived = !!tenant.archived_at

  useEffect(() => {
    if (action === null) {
      setConfirmName("")
      setConfirmPurge("")
      setConfirmIrreversible(false)
      setError(null)
      setIsSubmitting(false)
      setResultSummary(null)
      setCompletedAction(null)
    }
  }, [action])

  const close = () => {
    if (isSubmitting) return
    // After a successful lifecycle action we deferred `onChanged()` so the
    // parent didn't refetch (and re-mount the table — and this Dialog with
    // it) before the operator could read the `cleanup_summary`. Fire it now
    // on dismissal. For purge specifically, the refetch will drop the row
    // entirely (tenant hard-deleted), which is fine — the operator has
    // already seen the summary by the time they click Close.
    const finishedAction = completedAction
    setAction(null)
    if (finishedAction) {
      onChanged(finishedAction)
      if (isOwnTenant) {
        void queryClient.invalidateQueries({ queryKey: currentTenantQueryPrefix })
      }
    }
  }

  const run = async (verb: "archive" | "restore" | "purge") => {
    setIsSubmitting(true)
    setError(null)
    try {
      const res = await fetchWithAuth(`/api/tenants/${tenant.id}/${verb}`, {
        method: "POST",
      })
      const data = (await res.json().catch(() => ({}))) as {
        cleanup_summary?: CleanupStep[]
        code?: string
        error?: string
      }
      if (!res.ok) {
        // `cleanup_failed` from purge carries a cleanup_summary even at 400 —
        // surface it instead of a blank toast.
        if (data.code === "cleanup_failed" && data.cleanup_summary) {
          setResultSummary(data.cleanup_summary)
          setCompletedAction(verb)
          setError(tUi("cleanupFailedSeeDetailsBelowTenantWasNOT"))
        } else {
          setError(data.error || data.code || `Failed to ${verb} tenant`)
        }
        return
      }
      setResultSummary(data.cleanup_summary ?? [])
      setCompletedAction(verb)
      // `onChanged()` is deferred until the operator dismisses the dialog
      // (see `close()`). Firing it here would batch with these state
      // updates: the parent's `fetchTenants` sets `loading=true`
      // synchronously, which short-circuits the tenant table to a spinner
      // and unmounts this Dialog (Radix portal lifetime is tied to its
      // React parent). For purge the row never re-mounts — the tenant is
      // hard-deleted — so the `cleanup_summary` would be permanently
      // unreachable. Same defer benefits archive/restore visibility.
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${verb} tenant`)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <div className="flex items-center gap-1">
        {!isArchived ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setAction("archive")}
            title={tUi("archiveTenant")}
          >
            <Trash2 className="w-4 h-4 text-destructive" />
          </Button>
        ) : (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAction("restore")}
              title={tUi("restoreTenant")}
            >
              <ArchiveRestore className="w-4 h-4 text-emerald-600" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAction("purge")}
              title={tUi("purgeTenantIrreversible")}
            >
              <AlertTriangle className="w-4 h-4 text-destructive" />
            </Button>
          </>
        )}
      </div>

      {/* Archive dialog */}
      <Dialog open={action === "archive"} onOpenChange={(open) => !open && close()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              <span><span key={tUi("archive")}>{tUi("archive")}</span></span> {tenant.name}
            </DialogTitle>
            <DialogDescription>
              <span key={tUi("disablesAgentsVoiceEmailMessagingTearsDownPBX")}>{tUi("disablesAgentsVoiceEmailMessagingTearsDownPBX")}</span></DialogDescription>
          </DialogHeader>
          {completedAction === "archive" ? (
            <SummaryBlock
              summary={resultSummary}
              positive="Tenant archived."
            />
          ) : (
            <div className="space-y-2 py-2">
              <Label htmlFor="archive-confirm" className="text-destructive">
                <span><span key={tUi("type")}>{tUi("type")}</span></span> <span className="font-mono font-bold">{tenant.name}</span> <span><span key={tUi("toConfirm")}>{tUi("toConfirm")}</span></span>
              </Label>
              <Input
                id="archive-confirm"
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                placeholder={tUi("tenantName")}
                autoComplete="off"
                disabled={isSubmitting}
              />
              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={isSubmitting}>
              <span key={completedAction === "archive" ? tUi("close") : tCommonUi("cancel")}>{completedAction === "archive" ? tUi("close") : tCommonUi("cancel")}</span>
            </Button>
            {completedAction !== "archive" && (
              <Button
                variant="destructive"
                onClick={() => run("archive")}
                disabled={isSubmitting || confirmName !== tenant.name}
              >
                {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <span><span key={tUi("archiveTenant")}>{tUi("archiveTenant")}</span></span>
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore dialog */}
      <Dialog open={action === "restore"} onOpenChange={(open) => !open && close()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <ArchiveRestore className="w-5 h-5" />
              <span><span key={tUi("restore")}>{tUi("restore")}</span></span> {tenant.name}
            </DialogTitle>
            <DialogDescription>
              <span key={tUi("reopensTheTenantForSignInsAndRemoves")}>{tUi("reopensTheTenantForSignInsAndRemoves")}</span></DialogDescription>
          </DialogHeader>
          {completedAction === "restore" ? (
            <SummaryBlock
              summary={resultSummary}
              positive="Tenant restored."
            />
          ) : (
            <>
              {error && (
                <p className="text-sm text-destructive py-2">{error}</p>
              )}
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={isSubmitting}>
              <span key={completedAction === "restore" ? tUi("close") : tCommonUi("cancel")}>{completedAction === "restore" ? tUi("close") : tCommonUi("cancel")}</span>
            </Button>
            {completedAction !== "restore" && (
              <Button
                onClick={() => run("restore")}
                disabled={isSubmitting}
              >
                {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <span><span key={tUi("restoreTenant")}>{tUi("restoreTenant")}</span></span>
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purge dialog */}
      <Dialog open={action === "purge"} onOpenChange={(open) => !open && close()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              <span><span key={tUi("purge")}>{tUi("purge")}</span></span> {tenant.name}
            </DialogTitle>
            <DialogDescription>
              <span key={tUi("permanentlyDeletesTheTenantAndEveryRowThat")}>{tUi("permanentlyDeletesTheTenantAndEveryRowThat")}</span></DialogDescription>
          </DialogHeader>
          {completedAction === "purge" ? (
            <SummaryBlock
              summary={resultSummary}
              positive="Tenant purged."
            />
          ) : (
            <div className="space-y-3 py-2">
              <div className="space-y-2">
                <Label htmlFor="purge-name" className="text-destructive">
                  <span><span key={tUi("type")}>{tUi("type")}</span></span> <span className="font-mono font-bold">{tenant.name}</span> <span><span key={tUi("toConfirm")}>{tUi("toConfirm")}</span></span>
                </Label>
                <Input
                  id="purge-name"
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  placeholder={tUi("tenantName")}
                  autoComplete="off"
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="purge-literal" className="text-destructive">
                  <span><span key={tUi("type")}>{tUi("type")}</span></span> <span className="font-mono font-bold"><span key={tUi("pURGE")}>{tUi("pURGE")}</span></span> <span><span key={tUi("toConfirm")}>{tUi("toConfirm")}</span></span>
                </Label>
                <Input
                  id="purge-literal"
                  value={confirmPurge}
                  onChange={(e) => setConfirmPurge(e.target.value)}
                  placeholder={tUi("pURGE")}
                  autoComplete="off"
                  disabled={isSubmitting}
                />
              </div>
              <div className="flex items-start gap-2">
                <Checkbox
                  id="purge-irreversible"
                  checked={confirmIrreversible}
                  onCheckedChange={(checked) => setConfirmIrreversible(checked === true)}
                  disabled={isSubmitting}
                />
                <Label
                  htmlFor="purge-irreversible"
                  className="text-sm font-normal leading-snug"
                >
                  <span key={tUi("iUnderstandThisIsIrreversibleAndThatNo")}>{tUi("iUnderstandThisIsIrreversibleAndThatNo")}</span></Label>
              </div>
              {error && (
                <SummaryBlock summary={resultSummary} positive={null} errorHeader={error} />
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={isSubmitting}>
              <span key={completedAction === "purge" ? tUi("close") : tCommonUi("cancel")}>{completedAction === "purge" ? tUi("close") : tCommonUi("cancel")}</span>
            </Button>
            {completedAction !== "purge" && (
              <Button
                variant="destructive"
                onClick={() => run("purge")}
                disabled={
                  isSubmitting ||
                  confirmName !== tenant.name ||
                  confirmPurge !== "PURGE" ||
                  !confirmIrreversible
                }
              >
                {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <span><span key={tUi("purgeTenant")}>{tUi("purgeTenant")}</span></span>
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

interface SummaryBlockProps {
  summary: CleanupStep[] | null
  positive: string | null
  errorHeader?: string
}

function SummaryBlock({ summary, positive, errorHeader }: SummaryBlockProps) {
  if (!summary && !errorHeader) return null
  const hasFailure = summary?.some((step) => !step.ok) ?? false
  return (
    <div className="space-y-2 py-2 text-sm">
      {errorHeader && (
        <p className="text-destructive font-medium">{errorHeader}</p>
      )}
      {positive && !hasFailure && !errorHeader && (
        <p className="text-emerald-700 font-medium">{positive}</p>
      )}
      {summary && summary.length > 0 && (
        <ul className="space-y-1 rounded border border-border bg-muted/30 p-3 max-h-60 overflow-y-auto">
          {summary.map((step, idx) => (
            <li
              key={`${step.step}-${idx}`}
              className="flex items-start gap-2"
            >
              {step.ok ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
              ) : (
                <XCircle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="font-mono text-xs">{step.step}</div>
                {step.error && (
                  <div className="text-xs text-destructive break-words">
                    {step.error}
                  </div>
                )}
                {step.details && Object.keys(step.details).length > 0 && (
                  <div className={cn("text-xs text-muted-foreground break-words")}>
                    {JSON.stringify(step.details)}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
